import { inject, effect } from '@angular/core';
import { ThemeService } from '../../core/services/theme.service';
import { CalculatorDataService } from '../../core/services/calculator-data.service';
import { LoggingService } from '../../core/services/logging.service';
import { CalculatorTTMData } from '../../shared/models/calculator-ttm-data';

/**
 * Abstract base class for calculator components that consume TTM data.
 * Provides common service injections and reactive TTM data effect setup.
 * 
 * Subclasses should import the markFormGroupTouched utility from ./utils/form-validation.utils.ts
 * to maintain a consistent API across all calculator components.
 */
export abstract class BaseCalculatorComponent {
  // Theme service - centralized theme management
  themeService = inject(ThemeService);

  // Calculator data service for TTM data
  protected calculatorDataService = inject(CalculatorDataService);
  protected logger = inject(LoggingService);

  constructor() {
    // Set up effect to watch for TTM data changes and pre-populate form
    effect(() => {
      const ttmData = this.calculatorDataService.getTtmDataSignal()();
      if (ttmData && ttmData.ticker) {
        this.populateFormWithTtmData(ttmData);
      }
    });
  }

  /**
   * Populate form fields with TTM data from the calculator data service.
   * Each subclass must implement this to map TTM data to its specific form fields.
   */
  protected abstract populateFormWithTtmData(ttmData: CalculatorTTMData): void;
}
